import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import ModalRoot from '../../components/ModalRoot';

const CATEGORY_LABELS = {
  lighting_visibility: 'Lighting & Visibility',
  engine_fluids: 'Engine & Fluids',
  mechanical_systems: 'Mechanical Systems',
  tires_movement: 'Tires & Movement',
  tow_equipment: 'Tow Equipment',
  safety_equipment: 'Safety Equipment',
  condition_warnings: 'Condition & Warnings',
  dashboard_warnings: 'Dashboard Warning Lights',
};

const CATEGORY_ORDER = [
  'lighting_visibility',
  'engine_fluids',
  'mechanical_systems',
  'tires_movement',
  'tow_equipment',
  'safety_equipment',
  'condition_warnings',
  'dashboard_warnings',
];

function getCurrentShift() {
  const ct = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric',
    hour12: false,
  }).format(new Date());
  const hour = parseInt(ct, 10);
  return hour >= 7 && hour < 19 ? 'Day' : 'Night';
}

function getTodayCT() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}

/** Determine if a response value is the "negative" / flagged result */
function isNegativeResult(responseType, value) {
  if (!value) return false;
  if (responseType === 'pass_fail') return value === 'fail';
  if (responseType === 'ok_low') return value === 'low';
  if (responseType === 'yes_no') return value === 'yes';
  return false;
}

/** Button configs per response_type */
function getButtonConfig(responseType) {
  switch (responseType) {
    case 'ok_low':
      return {
        positive: { label: 'OK', value: 'ok', className: 'pass-btn' },
        negative: { label: 'LOW', value: 'low', className: 'fail-btn fail-btn--amber' },
      };
    case 'yes_no':
      return {
        positive: { label: 'NO', value: 'no', className: 'pass-btn' },
        negative: { label: 'YES', value: 'yes', className: 'fail-btn' },
      };
    case 'pass_fail':
    default:
      return {
        positive: { label: 'PASS', value: 'pass', className: 'pass-btn' },
        negative: { label: 'FAIL', value: 'fail', className: 'fail-btn' },
      };
  }
}

export default function InspectionFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [pageStatus, setPageStatus] = useState('loading'); // loading | already_completed | form | success | critical_failure
  const [existingInspectionId, setExistingInspectionId] = useState(null);
  const [items, setItems] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [error, setError] = useState('');

  // Form state
  const [truckId, setTruckId] = useState('');
  const [odometer, setOdometer] = useState('');
  const [responses, setResponses] = useState({});   // { itemId: 'pass'|'fail'|'ok'|'low'|'yes'|'no' }
  const [notes, setNotes] = useState({});            // { itemId: 'text' }
  const [photos, setPhotos] = useState({});           // { itemId: [File, ...] }
  const [warningDescription, setWarningDescription] = useState('');
  const [warningSeverity, setWarningSeverity] = useState('Low');
  const [warningPhotos, setWarningPhotos] = useState([]);
  const [generalComments, setGeneralComments] = useState('');
  const [certified, setCertified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const shift = getCurrentShift();
  const today = getTodayCT();

  // Group items by category
  const groupedItems = useCallback(() => {
    const groups = {};
    for (const cat of CATEGORY_ORDER) {
      groups[cat] = [];
    }
    for (const item of items) {
      const cat = item.category || 'safety_equipment';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    // Sort each group by sort_order
    for (const cat of Object.keys(groups)) {
      groups[cat].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }
    return groups;
  }, [items]);

  // Load data on mount
  useEffect(() => {
    async function init() {
      try {
        const [statusData, itemsData, trucksData] = await Promise.all([
          api.getMyInspectionStatus(),
          api.getInspectionItems(),
          api.getTrucks(),
        ]);

        const truckList = Array.isArray(trucksData) ? trucksData : (trucksData.trucks || []);
        setTrucks(truckList);

        // Pre-select assigned truck
        if (user && truckList.length > 0) {
          const assigned = truckList.find(t => t.assigned_driver_id === user.id);
          if (assigned) setTruckId(String(assigned.id));
        }

        const itemList = Array.isArray(itemsData) ? itemsData : (itemsData.items || []);
        setItems(itemList);

        if (statusData && statusData.completed) {
          setExistingInspectionId(statusData.inspection_id);
          setPageStatus('already_completed');
        } else {
          setPageStatus('form');
        }
      } catch (err) {
        setError(err.message || 'Failed to load inspection form');
        setPageStatus('form');
      }
    }
    init();
  }, [user]);

  // Response handlers
  function setItemResponse(itemId, value) {
    setResponses(prev => ({ ...prev, [itemId]: value }));
  }

  function clearNegativeData(itemId) {
    setNotes(prev => { const n = { ...prev }; delete n[itemId]; return n; });
    setPhotos(prev => { const n = { ...prev }; delete n[itemId]; return n; });
  }

  function handleResponseClick(item, value) {
    const wasNegative = isNegativeResult(item.response_type, responses[item.id]);
    setItemResponse(item.id, value);
    // Clear notes/photos when switching to positive
    if (wasNegative && !isNegativeResult(item.response_type, value)) {
      clearNegativeData(item.id);
    }
  }

  function handleNotes(itemId, text) {
    setNotes(prev => ({ ...prev, [itemId]: text }));
  }

  function handlePhotos(itemId, files) {
    setPhotos(prev => ({ ...prev, [itemId]: Array.from(files) }));
  }

  // Find the "warning lights active" item for special handling
  const warningLightsItem = items.find(
    i => i.category === 'dashboard_warnings' && /warning.?light/i.test(i.name)
  );
  const warningLightsActive = warningLightsItem && responses[warningLightsItem.id] === 'yes';

  // Find body damage item for payload
  const damageItem = items.find(
    i => i.category === 'condition_warnings' && /body.?damage|damage/i.test(i.name)
  );

  // Validation
  const allItemsAnswered = items.length > 0 && items.every(item => responses[item.id]);
  const warningLightsValid = !warningLightsActive || warningDescription.trim().length > 0;
  const canSubmit = truckId && odometer && allItemsAnswered && warningLightsValid && certified && !submitting;

  // Collect flagged items for success screen
  function getFlaggedItems() {
    return items.filter(item => isNegativeResult(item.response_type, responses[item.id]));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        truck_id: Number(truckId),
        odometer: Number(odometer),
        responses: items.map(item => ({
          item_id: item.id,
          result: responses[item.id],
          notes: isNegativeResult(item.response_type, responses[item.id])
            ? (notes[item.id] || null)
            : null,
        })),
        has_body_damage: damageItem && responses[damageItem.id] === 'yes' ? 1 : 0,
        body_damage_notes: damageItem ? (notes[damageItem.id] || null) : null,
        warning_lights_detail: warningLightsActive
          ? JSON.stringify({ description: warningDescription, severity: warningSeverity })
          : null,
        comments: generalComments || null,
      };

      const result = await api.submitInspection(payload);
      const inspectionId = result.id || result.inspection_id;

      // Upload photos
      if (inspectionId) {
        const uploads = [];
        for (const [itemId, files] of Object.entries(photos)) {
          if (files && files.length > 0) {
            const fd = new FormData();
            fd.append('item_id', itemId);
            files.forEach(f => fd.append('photos', f));
            uploads.push(api.uploadInspectionPhotos(inspectionId, fd));
          }
        }
        if (warningPhotos.length > 0) {
          const fd = new FormData();
          fd.append('type', 'warning_lights');
          warningPhotos.forEach(f => fd.append('photos', f));
          uploads.push(api.uploadInspectionPhotos(inspectionId, fd));
        }
        if (uploads.length > 0) {
          await Promise.all(uploads).catch(() => {});
        }
      }

      const flagged = getFlaggedItems();
      // Server returns critical_failure / truck_unit / failed_items / work_order
      // when ANY is_critical inspection item failed. The driver MUST see a
      // blocking "this truck cannot be driven" screen before doing anything
      // else — silent grounding is dangerous.
      setSubmitResult({
        inspectionId,
        flaggedItems: flagged,
        critical_failure: !!result?.critical_failure,
        truck_unit: result?.truck_unit || null,
        failed_critical_items: result?.failed_items || [],
        work_order: result?.work_order || null,
      });
      setPageStatus(result?.critical_failure ? 'critical_failure' : 'success');
    } catch (err) {
      setError(err.message || 'Failed to submit inspection');
    }
    setSubmitting(false);
  }

  // --- RENDER ---

  if (pageStatus === 'loading') {
    return (
      <div className="inspection-form">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (pageStatus === 'already_completed') {
    return (
      <div className="inspection-form">
        <div className="inspection-form__complete">
          <div className="inspection-form__complete-icon">&#10003;</div>
          <h2>Inspection Already Completed</h2>
          <p>You've already completed your inspection for this shift.</p>
          {existingInspectionId && (
            <button
              className="btn btn-outline"
              onClick={() => navigate(`/maintenance/inspections/${existingInspectionId}`)}
              style={{ marginTop: '16px' }}
            >
              View Your Inspection
            </button>
          )}
          <button
            className="btn btn-outline"
            onClick={() => navigate('/maintenance')}
            style={{ marginTop: '8px' }}
          >
            Back to Maintenance
          </button>
        </div>
      </div>
    );
  }

  // ── CRITICAL FAILURE SCREEN ──────────────────────────────────────
  // Full-bleed red warning. No "Back" link. Driver acknowledges by
  // tapping the explicit confirm, which routes them to maintenance.
  if (pageStatus === 'critical_failure') {
    const truckLabel = submitResult?.truck_unit ? `#${submitResult.truck_unit}` : '';
    const failedItems = submitResult?.failed_critical_items || [];
    const wo = submitResult?.work_order;
    return (
      <ModalRoot className="" style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: '#dc2626', color: '#fff',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: 24, overflowY: 'auto',
      }}>
        {/* margin: auto centers it but, unlike justify-content: center, never
            pushes the top out of reach on a short phone screen. */}
        <div style={{ maxWidth: 560, width: '100%', textAlign: 'center', margin: 'auto 0' }}>
          <div style={{ fontSize: 96, lineHeight: 1, marginBottom: 16 }}>🛑</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: 0, letterSpacing: 0.5 }}>
            TRUCK CANNOT BE DRIVEN
          </h1>
          <p style={{ fontSize: 20, fontWeight: 700, margin: '16px 0 8px' }}>
            This truck has a critical item failure and is OUT OF SERVICE.
          </p>
          {truckLabel && (
            <div style={{
              fontSize: 36, fontWeight: 900, marginTop: 12, marginBottom: 16,
              padding: '10px 20px', background: 'rgba(0,0,0,0.25)', borderRadius: 12, display: 'inline-block',
            }}>
              Truck {truckLabel}
            </div>
          )}
          {failedItems.length > 0 && (
            <div style={{
              background: 'rgba(0,0,0,0.25)', borderRadius: 12, padding: 16,
              marginTop: 16, textAlign: 'left',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, opacity: 0.85 }}>
                Failed critical item{failedItems.length === 1 ? '' : 's'}:
              </div>
              <ul style={{ margin: 0, paddingLeft: 24, fontSize: 18, lineHeight: 1.6, fontWeight: 700 }}>
                {failedItems.map((name, i) => <li key={i}>{name}</li>)}
              </ul>
            </div>
          )}
          {wo && (
            <div style={{
              background: 'rgba(0,0,0,0.25)', borderRadius: 12, padding: 16,
              marginTop: 16, fontSize: 14, fontWeight: 600,
            }}>
              Work Order #{wo.id} created — priority {(wo.priority || 'urgent').toUpperCase()}.<br />
              Maintenance has been notified by SMS.
            </div>
          )}
          <p style={{ fontSize: 14, marginTop: 24, opacity: 0.9 }}>
            DO NOT operate this truck. Notify your dispatcher and get a different truck for your shift.
          </p>
          <button
            onClick={() => navigate('/maintenance')}
            style={{
              marginTop: 24, padding: '14px 32px', fontSize: 16, fontWeight: 800,
              background: '#fff', color: '#dc2626', border: 'none', borderRadius: 8,
              cursor: 'pointer', textTransform: 'uppercase', letterSpacing: 0.5,
              minWidth: 240,
            }}
          >
            I understand
          </button>
        </div>
      </ModalRoot>
    );
  }

  if (pageStatus === 'success') {
    const flagged = submitResult?.flaggedItems || [];
    return (
      <div className="inspection-form">
        <div className="inspection-form__complete">
          <div className={`inspection-form__complete-icon ${flagged.length > 0 ? 'inspection-form__complete-icon--warning' : ''}`}>
            {flagged.length > 0 ? '!' : '\u2713'}
          </div>
          <h2>Inspection Submitted Successfully</h2>
          {flagged.length > 0 && (
            <div className="inspection-form__warning">
              <p><strong>{flagged.length} item(s) flagged for review:</strong></p>
              <ul style={{ textAlign: 'left', margin: '8px auto', maxWidth: '320px' }}>
                {flagged.map(item => (
                  <li key={item.id}>{item.name}</li>
                ))}
              </ul>
            </div>
          )}
          <button
            className="btn btn-gold"
            onClick={() => navigate('/maintenance')}
            style={{ marginTop: '16px' }}
          >
            Back to Maintenance
          </button>
        </div>
      </div>
    );
  }

  // --- MAIN FORM ---
  const groups = groupedItems();

  return (
    <div className="inspection-form">
      <div className="inspection-form__header">
        <h1>Daily Truck Inspection</h1>
        <div className="inspection-form__shift-info">
          <span className="inspection-form__shift-badge">{shift} Shift</span>
          <span>{today}</span>
        </div>
        {user && <p style={{ margin: '4px 0 0', opacity: 0.7 }}>Driver: {user.name}</p>}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Truck & Odometer */}
        <div className="inspection-form__section">
          <div className="form-grid">
            <div className="form-group">
              <label>Truck / Unit Number *</label>
              <select
                value={truckId}
                onChange={e => setTruckId(e.target.value)}
                required
                className="form-input"
              >
                <option value="">Select Truck</option>
                {trucks.filter(t => t.status === 'active').map(t => (
                  <option key={t.id} value={t.id}>
                    {t.unit_number} — {[t.make, t.model].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Odometer Reading *</label>
              <input
                type="number"
                value={odometer}
                onChange={e => setOdometer(e.target.value)}
                required
                min="0"
                className="form-input"
                placeholder="Current mileage"
              />
            </div>
          </div>
        </div>

        {/* Grouped Checklist */}
        {CATEGORY_ORDER.map(category => {
          const categoryItems = groups[category];
          if (!categoryItems || categoryItems.length === 0) return null;

          return (
            <div key={category}>
              <div className="inspection-form__divider">
                {CATEGORY_LABELS[category] || category}
              </div>
              <div className="inspection-form__checklist">
                {categoryItems.map(item => {
                  const btnConfig = getButtonConfig(item.response_type);
                  const currentValue = responses[item.id];
                  const negative = isNegativeResult(item.response_type, currentValue);
                  const isDashboardWarning = item.category === 'dashboard_warnings';
                  const isWarningLightsMainItem = warningLightsItem && item.id === warningLightsItem.id;

                  return (
                    <div
                      key={item.id}
                      className={`inspection-form__item ${negative ? 'inspection-form__item--fail' : ''}`}
                    >
                      <div className="inspection-form__item-row">
                        <span className="inspection-form__item-name">{item.name}</span>
                        <div className="inspection-form__item-buttons">
                          <button
                            type="button"
                            className={`${btnConfig.positive.className} ${currentValue === btnConfig.positive.value ? 'active' : ''}`}
                            onClick={() => handleResponseClick(item, btnConfig.positive.value)}
                          >
                            {btnConfig.positive.label}
                          </button>
                          <button
                            type="button"
                            className={`${btnConfig.negative.className} ${currentValue === btnConfig.negative.value ? 'active' : ''}`}
                            onClick={() => handleResponseClick(item, btnConfig.negative.value)}
                          >
                            {btnConfig.negative.label}
                          </button>
                          {item.allow_na === 1 && (
                            <button
                              type="button"
                              className={`na-btn ${currentValue === 'na' ? 'active' : ''}`}
                              onClick={() => handleResponseClick(item, 'na')}
                            >
                              N/A
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Expanded notes + photo on negative response */}
                      {negative && !isWarningLightsMainItem && (
                        <div className="inspection-form__fail-details">
                          <textarea
                            className="form-input"
                            placeholder={
                              isDashboardWarning
                                ? 'Describe the warning light...'
                                : item.response_type === 'ok_low'
                                  ? 'Describe the low level...'
                                  : 'Describe the issue...'
                            }
                            value={notes[item.id] || ''}
                            onChange={e => handleNotes(item.id, e.target.value)}
                            rows={2}
                          />
                          <div className="inspection-form__photo-upload">
                            <label className="btn btn-sm btn-outline">
                              Add Photos
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                hidden
                                onChange={e => handlePhotos(item.id, e.target.files)}
                              />
                            </label>
                            {photos[item.id] && photos[item.id].length > 0 && (
                              <span className="inspection-form__photo-count">
                                {photos[item.id].length} photo(s) selected
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Dashboard Warning Lights special expanded section */}
                      {isWarningLightsMainItem && warningLightsActive && (
                        <div className="inspection-form__fail-details">
                          <div className="form-group">
                            <label>Warning Light Description *</label>
                            <textarea
                              className="form-input"
                              placeholder="Describe which warning lights are active..."
                              value={warningDescription}
                              onChange={e => setWarningDescription(e.target.value)}
                              rows={3}
                              required
                            />
                          </div>
                          <div className="form-group" style={{ marginTop: '8px' }}>
                            <label>Severity</label>
                            <select
                              className="form-input"
                              value={warningSeverity}
                              onChange={e => setWarningSeverity(e.target.value)}
                            >
                              <option value="Low">Low</option>
                              <option value="Medium">Medium</option>
                              <option value="High">High</option>
                            </select>
                          </div>
                          <div className="inspection-form__photo-upload" style={{ marginTop: '8px' }}>
                            <label className="btn btn-sm btn-outline">
                              Add Photos (Recommended)
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                hidden
                                onChange={e => setWarningPhotos(Array.from(e.target.files))}
                              />
                            </label>
                            {warningPhotos.length > 0 && (
                              <span className="inspection-form__photo-count">
                                {warningPhotos.length} photo(s) selected
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* General Comments */}
        <div className="inspection-form__divider">Comments</div>
        <div className="inspection-form__section">
          <textarea
            className="form-input"
            placeholder="Any additional comments or observations (optional)"
            value={generalComments}
            onChange={e => setGeneralComments(e.target.value)}
            rows={5}
            style={{ minHeight: '120px', width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        {/* Driver Sign-Off */}
        <div className="inspection-form__certification">
          <label className="inspection-form__certify-label">
            <input
              type="checkbox"
              checked={certified}
              onChange={e => setCertified(e.target.checked)}
            />
            <span>I certify this inspection is accurate and complete.</span>
          </label>
        </div>

        {/* Submit */}
        <div className="inspection-form__actions">
          <button type="button" className="btn btn-outline" onClick={() => navigate('/maintenance')}>
            Cancel
          </button>
          <button type="submit" className="btn btn-gold btn-lg" disabled={!canSubmit}>
            {submitting ? 'Submitting...' : 'Submit Inspection'}
          </button>
        </div>

        {/* Validation hints */}
        {!canSubmit && !submitting && (
          <p className="inspection-form__hint">
            {!truckId && 'Select a truck. '}
            {!odometer && 'Enter odometer reading. '}
            {!allItemsAnswered && 'Answer all checklist items. '}
            {!warningLightsValid && 'Describe active warning lights. '}
            {!certified && 'Check the certification box.'}
          </p>
        )}
      </form>
    </div>
  );
}
